import moment from 'moment';
import { CartServiceType } from '../store/shoppingCart/shoppingCartStore';
import type { AppliedCouponResponseDTO } from '../DTO/checkoutResponseDTO';
import { useToken } from '../store/auth/tokenStore';
import { openWithPassage } from './passageToken';

export const toTitleString = (rawString: string) => {
    return rawString[0].toUpperCase() + rawString.slice(1);
};

export const addDaysToDate = (date: Date, numberOfDays: number) => {
    const newValue = moment(date);
    newValue.add({ day: numberOfDays });
    return newValue.toDate();
};

export const addMonthsToDate = (date: Date, numberOfMonths: number) => {
    const newValue = moment(date);
    newValue.add({ month: numberOfMonths });
    return newValue.toDate();
};


export enum WaveLevelEnum {
    BAY = 'Bay',
    REEF = 'Reef',
}

export enum SectionsEnum {
    SECTIONS = 'Sessões',
    TIKECETS = 'Ingressos',
    // SERVICES = 'Serviços',
}

export const sectionsConst = Object.values(SectionsEnum);

export function redirectToWhatsapp(exhibitionName: string) {
    window.open(
        `https://wa.me/${import.meta.env.VITE_SURFLAND_NUMBER}?text=Olá,%20tenho%20dúvidas%20sobre%20a%20exibição%20${encodeURIComponent(exhibitionName)}`,
        '_blank'
    );
}

export function getCartType(cartType: CartServiceType): string {
    switch (cartType) {
        case CartServiceType.Waves:
            return 'wave';
        case CartServiceType.Tickets:
            return 'ticket';
        case CartServiceType.Park:
            return 'park';
        default:
            return 'service';
    }
}

/**
 * Abre o portal do cliente numa aba nova.
 *
 * Vai sempre pelo token de passagem (`openWithPassage`, sem flag): abre a aba na hora
 * do clique (com `opener` cortado), pede um token curto em
 * `POST /api/Passage/Issue` (JWT só no cabeçalho, prazo de 4 s) e leva a aba
 * para `VITE_OWNER_URL#message=redirect.token.lp&data=<token curto>`. Regra de
 * falha igual à do portal (#59):
 * - 401: a sessão local acabou; ela é apagada e o portal abre sem token;
 * - 404, 405, 5xx, rede, CORS ou timeout: usa o link de hoje,
 *   `VITE_OWNER_URL?message=redirect.token.lp&data=<JWT>`, e registra o motivo
 *   com `console.warn`, nunca o token. É o caso da API sem as chaves
 *   `PassageToken` (produção hoje): não há 401 nem logout.
 *
 * Sem sessão, abre o cadastro do portal, como antes.
 */
export function redirectOwnerSearch(token?: string) {
    openOwnerPortal(import.meta.env.VITE_OWNER_URL, token);
}

/** Igual a {@link redirectOwnerSearch}, mas abre a página de ingressos (`/ticket`). */
export function redirectYourTickets(token?: string) {
    openOwnerPortal(import.meta.env.VITE_OWNER_URL + '/ticket', token);
}

function openOwnerPortal(target: string, token?: string) {
    const url = new URL(target);

    if (!token) {
        url.searchParams.append('message', 'create-account');
        window.open(url.toString(), '_blank');
        return;
    }

    const targetUrl = url.toString();
    // O link de hoje, montado do mesmo jeito de antes.
    url.searchParams.append('message', 'redirect.token.lp');
    url.searchParams.append('data', token);
    const legacyUrl = url.toString();

    void openWithPassage(window, {
        apiBaseUrl: `${import.meta.env.VITE_API_URL}/api`,
        sessionJwt: token,
        targetUrl,
        legacyUrl,
        onUnauthorized: () => useToken.getState().setToken(null),
    });
}

export function validateCPFCNPJ(value: string, onlyCPF?: boolean): boolean {
    value = value.replace(/\D/g, '');
  
    if(value.length === 11 && !/^(\d)\1{10}$/.test(value)) {  
        let soma = 0;
        let peso = 10;
        for (let i = 0; i < 9; i++) {
        soma += parseInt(value[i]) * peso--;
        }
        let resto = soma % 11;
        const digito1 = resto < 2 ? 0 : 11 - resto;
        if (parseInt(value[9]) !== digito1) {
        return false;
        }
    
        soma = 0;
        peso = 11;
        for (let i = 0; i < 10; i++) {
        soma += parseInt(value[i]) * peso--;
        }
        resto = soma % 11;
        const digito2 = resto < 2 ? 0 : 11 - resto;
        return parseInt(value[10]) === digito2;
    } else if(!onlyCPF && value.length === 14 && !/^(\d)\1{13}$/.test(value)) {
        value = value.replace(/\D/g, '');
    
        if (value.length !== 14 || /^(\d)\1{13}$/.test(value)) {
        return false;
        }
    
        let soma = 0;
        const peso = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        for (let i = 0; i < 12; i++) {
        soma += parseInt(value[i]) * peso[i + 1];
        }
        let resto = soma % 11;
        const digito1 = resto < 2 ? 0 : 11 - resto;
        if (parseInt(value[12]) !== digito1) {
        return false;
        }
    
        soma = 0;
        for (let i = 0; i < 13; i++) {
        soma += parseInt(value[i]) * peso[i];
        }
        resto = soma % 11;
        const digito2 = resto < 2 ? 0 : 11 - resto;
        return parseInt(value[13]) === digito2;
    }

    return false;
}
/**
 * Calcula o valor efetivo do desconto baseado no cupom aplicado
 * @param coupon - O cupom aplicado
 * @param cartTotal - O valor total do carrinho
 * @returns O valor do desconto em reais
 */
export function calculateEffectiveDiscount(
    coupon: AppliedCouponResponseDTO | null,
    cartTotal: number
): number {
    if (!coupon) return 0;
    
    if (coupon.discountValue > 0) {
        return coupon.discountValue;
    }
    
    if (coupon.percentage && coupon.percentage > 0) {
        return (cartTotal * coupon.percentage) / 100;
    }
    
    return 0;
}